import { API } from "@/lib/api";

/** Visual card for ONE character or reference item.
 *  Props: item={id,title,meta,body,image_url}, sectionKey, onChange, onRemove, onAddImage
 */
export default function SubItemCard({ item, sectionKey, onChange, onRemove, onPickImage }) {
  const cfg = {
    personajes: {
      titlePlaceholder: "Nombre del personaje",
      metaPlaceholder: "Edad · función dramática",
      bodyPlaceholder: "Descripción: rasgo físico, dimensión emocional, relación con el conflicto…",
      bodyRows: 4,
      showImage: true,
    },
    referencias: {
      titlePlaceholder: "Título de la obra",
      metaPlaceholder: "Director/a, año · país",
      bodyPlaceholder: "1-3 frases sobre la conexión específica (visual, narrativa, temática) con este proyecto…",
      bodyRows: 4,
      showImage: true,
    },
    biofilmo: {
      titlePlaceholder: "Nombre de la persona o productora",
      metaPlaceholder: "Cargo (Director/a, Guionista, Productor/a, DOP, Montaje…)",
      bodyPlaceholder: "Bio + obra previa relevante. Premios y festivales si aporta.",
      bodyRows: 5,
      showImage: true,
    },
    cartas: {
      titlePlaceholder: "Institución / empresa / persona firmante",
      metaPlaceholder: "Tipo de apoyo (Coproducción · Distribución · Cesión de archivo…)",
      bodyPlaceholder: "Texto de la carta de intención / apoyo.",
      bodyRows: 10,
      showImage: false,
    },
  }[sectionKey] || {
    titlePlaceholder: "Título",
    metaPlaceholder: "Meta",
    bodyPlaceholder: "Descripción",
    bodyRows: 4,
    showImage: true,
  };

  const hasImg = Boolean(item.image_url);

  return (
    <article data-testid={`subitem-${item.id}`}
             className="overflow-hidden rounded-xl border border-stone-300/70 bg-white shadow-sm transition hover:shadow-md">
      <div className={`grid grid-cols-1 ${cfg.showImage ? "md:grid-cols-[140px,1fr]" : ""}`}>
        {/* Image / placeholder */}
        {cfg.showImage && (
          <button onClick={onPickImage} data-testid={`subitem-img-${item.id}`}
                  className="group relative aspect-[4/3] w-full overflow-hidden bg-[#F1EEE5]/40 text-stone-400 md:aspect-auto md:h-full">
            {hasImg ? (
              <img src={item.image_url.startsWith("data:") ? item.image_url : `${API.replace('/api','')}${item.image_url}`}
                   alt="" className="h-full w-full object-cover transition group-hover:scale-105" />
            ) : (
              <div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-1 text-stone-500">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" className="h-7 w-7">
                  <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" />
                  <path d="M21 15l-5-5L5 21" strokeLinejoin="round" />
                </svg>
                <span className="text-[10px] uppercase tracking-widest">+ Imagen</span>
              </div>
            )}
            {hasImg && (
              <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-stone-900/0 px-2 py-1 text-[9px] uppercase tracking-widest text-white opacity-0 transition group-hover:bg-stone-900/60 group-hover:opacity-100">
                Cambiar imagen
              </span>
            )}
          </button>
        )}

        {/* Content */}
        <div className="flex flex-col gap-2 p-4">
          <div className="flex items-start justify-between gap-2">
            <input value={item.title || ""}
                   onChange={(e) => onChange({ ...item, title: e.target.value })}
                   placeholder={cfg.titlePlaceholder}
                   data-testid={`subitem-title-${item.id}`}
                   className="min-w-0 flex-1 bg-transparent font-serif text-lg text-stone-900 outline-none placeholder:text-stone-300 focus:border-b focus:border-[#26775E]" />
            <button onClick={onRemove} data-testid={`subitem-remove-${item.id}`}
                    title="Eliminar"
                    className="shrink-0 rounded-full p-1 text-stone-400 transition hover:bg-red-50 hover:text-red-600">
              ×
            </button>
          </div>
          <input value={item.meta || ""}
                 onChange={(e) => onChange({ ...item, meta: e.target.value })}
                 placeholder={cfg.metaPlaceholder}
                 data-testid={`subitem-meta-${item.id}`}
                 className="bg-transparent text-xs uppercase tracking-widest text-[#26775E] outline-none placeholder:text-stone-300 placeholder:normal-case placeholder:tracking-normal" />
          <textarea value={item.body || ""}
                    onChange={(e) => onChange({ ...item, body: e.target.value })}
                    placeholder={cfg.bodyPlaceholder}
                    rows={cfg.bodyRows}
                    data-testid={`subitem-body-${item.id}`}
                    className="w-full resize-y rounded border border-stone-200 bg-[#FBFAF4] p-3 font-serif text-[13px] leading-relaxed text-stone-800 outline-none focus:border-[#26775E] focus:bg-white" />
        </div>
      </div>
    </article>
  );
}
